/* eslint-disable @typescript-eslint/no-use-before-define */
import 'styled-components/macro'
import { Fragment, type MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isPlainObject } from 'remeda'
import { ThemeProvider } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'
import { QuickAdd } from '@components/QuickAdd/QuickAdd'
import { dataTestIds, seasonsMap } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { FallWinterSeasonIcon } from '@velasca/icons/fall-winter-season'
import { FourSeasonIcon } from '@velasca/icons/four-season'
import { SpringSummerSeasonIcon } from '@velasca/icons/spring-summer-season'
import type {
  ApparelProductProps,
  Product,
  ProductSize,
  StaffPickIcon,
  Status,
} from '@velasca/types'
import { PriceRow } from '@velasca/ui/price-row'
import { QuickAddToCartDesktop } from '@velasca/ui/quick-add-to-cart-desktop'
import { VerticalTextSeparator } from '@velasca/ui/vertical-text-separator'
import { buildDprSources } from '@velasca/utils/build-dpr-sources'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { getSizeGuideUrl } from '@velasca/utils/get-size-guide-url'
import { getThemeByBrand } from '@velasca/utils/get-theme-by-brand'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import ApparelProductSkeleton from '../ApparelProductSkeleton'
import ColorSelector from '../ColorSelector'
import { useExchangeRates } from '../ExchangeRatesProvider'
import { useStaffPickImage } from '../StaffPickImageProvider'
import {
  AbsoluteQuickAddToCartButton,
  ApparelCard,
  ApparelVariants,
  Description,
  DescriptionBlock,
  DescriptionBold,
  DescriptionThin,
  FrontImgWrapper,
  HoverImg,
  HoverImgWrapper,
  Images,
  Img,
  Link,
  LinkFlex,
  Price,
  ProductInfo,
  ProductReviewBadge,
  Season,
  SeasonIcon,
  SeasonLabel,
  Separator,
  SlideUp,
  StaffPickImage,
  StyledProductBranding,
  Title,
  UpperSection,
} from './ApparelProduct.styles'

export const ApparelProduct = ({
  title,
  showProductType = false,
  productType,
  isGiftCard = false,
  variants = [],
  defaultSelected = 0,
  loading = false,
  showSingleVariant = false,
  size = '',
  sizes,
  showReviewBadge = false,
  showSeason = false,
  openInNewTab = false,
  collectionBrandID,
  isQuickAdd = false,
  onQuickAdd,
  showBrandingOnWornImage = false,
  onAddToCart,
  shouldOpenCartDrawerOnAddToCart = true,
}: ApparelProductProps) => {
  const { t } = useTranslation('product')
  const [isQuickAddPopupOpen, setIsQuickAddPopupOpen] = useState<boolean>(false)
  const [isQuickAddDesktopOpen, setIsQuickAddDesktopOpen] =
    useState<boolean>(false)
  // SHOP-3508: Refactoring useStaffPickIcon
  const { staffPickIcons, settings } = useStaffPickImage()
  const { convert } = useExchangeRates()
  const isSmallerThan768px = useMediaQuery('(max-width: 767px)')
  const [selectedVariant, setSelectedVariant] = useState(
    variants[defaultSelected],
  )

  const sizeGuideUrl = useMemo(
    () => getSizeGuideUrl(selectedVariant?.handle),
    [selectedVariant],
  )

  const description = selectedVariant?.description
  const jolly = selectedVariant?.jolly
  const staffPickIcon = useMemo(() => {
    if (staffPickIcons && !loading) {
      const currentStaffPickIcon = staffPickIcons.find(
        (icon: StaffPickIcon) => icon.id === selectedVariant?.staffPickIconID,
      )

      if (currentStaffPickIcon) {
        return currentStaffPickIcon
      }

      // If product is not new, there is no point in looking for a new icon
      if (!selectedVariant.isNew) {
        return
      }

      if (!settings.newID) {
        handleError(
          new Error(
            'Product is new but there is no newID defined. Define a newID in the CMS',
          ),
        )

        return undefined
      }

      const newIcon = staffPickIcons.find(
        (icon: StaffPickIcon) => icon?.id === settings.newID,
      )

      if (!newIcon) {
        handleError(
          new Error('No new staff pick icon found, plese add it to cms'),
        )
      }

      return newIcon
    }

    return undefined
  }, [staffPickIcons, settings, selectedVariant, loading])

  const price = useMemo(() => {
    const minPrice = isPlainObject(selectedVariant?.price)
      ? selectedVariant?.price?.min * 100
      : 0
    const maxPrice = isPlainObject(selectedVariant?.price)
      ? selectedVariant?.price?.max * 100
      : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [selectedVariant, convert])

  const compareAtPrice = useMemo(() => {
    const minPrice = isPlainObject(selectedVariant?.compareAtPrice)
      ? selectedVariant?.compareAtPrice?.min * 100
      : 0
    const maxPrice = isPlainObject(selectedVariant?.compareAtPrice)
      ? selectedVariant?.compareAtPrice?.max * 100
      : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [selectedVariant, convert])

  const isPriceDiscounted = useMemo(() => {
    const nomalizedCompareAtPrice = isPlainObject(
      selectedVariant?.compareAtPrice,
    )
      ? selectedVariant?.compareAtPrice?.min
      : 0
    const nomalizedPrice = isPlainObject(selectedVariant?.price)
      ? selectedVariant?.price?.max
      : 0

    return nomalizedCompareAtPrice > nomalizedPrice
  }, [selectedVariant])

  if (loading) {
    return (
      <ApparelProductSkeleton
        size={size}
        showProductType={showProductType}
        showSingleVariant={showSingleVariant}
        showReviewBadge={showReviewBadge}
        showSeason={showSeason}
      />
    )
  }

  return (
    <ThemeProvider theme={getThemeByBrand(collectionBrandID)}>
      <ApparelCard
        $isQuickAdd={isQuickAdd}
        className={size}
        onMouseEnter={() => {
          if (!isQuickAdd) {
            return
          }

          setIsQuickAddDesktopOpen(true)
          !isQuickAddDesktopOpen &&
            !isSmallerThan768px &&
            onQuickAdd &&
            onQuickAdd(selectedVariant.handle ?? '')
        }}
        onMouseLeave={() => {
          if (!isQuickAdd) {
            return
          }
          setIsQuickAddDesktopOpen(false)
        }}
      >
        <UpperSection>
          {selectedVariant.handle ? (
            <Link
              href={`/products/${selectedVariant.handle}`}
              target={openInNewTab ? '_blank' : '_self'}
            >
              <Images>
                {variants.map(({ id, img, collectionBrandName }) => {
                  return (
                    <Fragment key={id}>
                      {onQuickAdd && selectedVariant?.handle && (
                        <AbsoluteQuickAddToCartButton
                          onClick={(e: MouseEvent<HTMLElement>) => {
                            e.preventDefault()
                            setIsQuickAddPopupOpen(true)
                            onQuickAdd(selectedVariant.handle ?? '')
                          }}
                        />
                      )}
                      {!collectionBrandName && staffPickIcon?.image && (
                        <StaffPickImage
                          visible={selectedVariant.id === id}
                          alt={staffPickIcon?.image?.alt}
                          imgSrc={setQueryParams(staffPickIcon?.image?.src, [
                            { name: 'width', value: '100' },
                          ])}
                          sources={buildDprSources(staffPickIcon?.image?.src, [
                            { width: 100, dpr: 1 },
                            { width: 200, dpr: 2 },
                            { width: 300, dpr: 3 },
                          ])}
                        />
                      )}
                      {img?.hover && (
                        <HoverImgWrapper visible={selectedVariant.id === id}>
                          <HoverImg
                            lazyload
                            lqip
                            showDefault
                            draggable={false}
                            sources={buildIntrinsicWidthSources({
                              src: img.hover,
                            })}
                            imgSrc={setQueryParams(img.hover, [
                              { name: 'width', value: '30' },
                            ])}
                            alt={t('apparelProductHoverImgAltTemplate', {
                              material: description?.thin,
                              color: description?.bold,
                              productTitle: title,
                              productType,
                            })}
                          />
                          {showBrandingOnWornImage && collectionBrandName && (
                            <StyledProductBranding
                              title={collectionBrandName}
                            />
                          )}
                        </HoverImgWrapper>
                      )}
                      <FrontImgWrapper
                        visible={selectedVariant.id === id}
                        className={!img?.hover ? 'single' : ''}
                      >
                        {img?.front && (
                          <>
                            <Img
                              lazyload
                              lqip
                              showDefault
                              draggable={false}
                              transitionDuration={50}
                              imgSrc={setQueryParams(img.front, [
                                { name: 'width', value: '30' },
                              ])}
                              sources={buildIntrinsicWidthSources({
                                src: img.front,
                              })}
                              alt={t('apparelProductFrontImgAltTemplate', {
                                material: description?.thin,
                                color: description?.bold,
                                productTitle: title,
                                productType,
                              })}
                            />
                            {!showBrandingOnWornImage &&
                              collectionBrandName && (
                                <StyledProductBranding
                                  title={collectionBrandName}
                                />
                              )}
                          </>
                        )}
                      </FrontImgWrapper>
                    </Fragment>
                  )
                })}
              </Images>
            </Link>
          ) : null}
          {!isSmallerThan768px && isQuickAdd && (
            <QuickAddItemDesktopWrapper
              shouldOpenCartDrawerOnAddToCart={shouldOpenCartDrawerOnAddToCart}
              title={t('quickAdd')}
              sizeGuideUrl={sizeGuideUrl}
              sizeGuideText={t('sizeLink')}
              sizes={sizes}
              isOpen={isQuickAddDesktopOpen}
              isGiftCard={isGiftCard}
              handle={selectedVariant.handle}
              onAddToCart={onAddToCart}
            />
          )}
        </UpperSection>
        <LinkFlex
          href={`/products/${selectedVariant.handle ?? ''}`}
          target={openInNewTab ? '_blank' : '_self'}
        >
          <Title>{title}</Title>

          {showReviewBadge &&
            variants.map(({ id, reviews }) => (
              <ProductReviewBadge
                key={id}
                starSize={14}
                current={selectedVariant.id === id}
                reviewsCount={reviews?.count}
                reviewsAverage={reviews?.average}
                short={
                  size === 'xs' || size === 'lg' || size === 'dynamic-size'
                }
              />
            ))}
          {showProductType && productType && (
            <ProductInfo>{productType}</ProductInfo>
          )}
          {showSeason &&
            selectedVariant?.seasonID &&
            selectedVariant?.seasonName && (
              <Season>
                <SeasonIcon>
                  {selectedVariant.seasonID === seasonsMap.SPRING_SUMMER && (
                    <SpringSummerSeasonIcon />
                  )}
                  {selectedVariant.seasonID === seasonsMap.FALL_WINTER && (
                    <FallWinterSeasonIcon />
                  )}
                  {/* {selectedVariant.seasonID === seasons.THREE_SEASONS && null} */}
                  {selectedVariant.seasonID === seasonsMap.ALL_SEASONS && (
                    <FourSeasonIcon />
                  )}
                </SeasonIcon>
                <SeasonLabel>{selectedVariant.seasonName}</SeasonLabel>
              </Season>
            )}
          {(description || jolly) && (
            <Description>
              {description?.bold && (
                <DescriptionBold>{description.bold}</DescriptionBold>
              )}

              {description?.bold && description.thin && (
                <VerticalTextSeparator />
              )}

              {description?.thin && (
                <DescriptionThin>{description.thin}</DescriptionThin>
              )}

              {jolly && <DescriptionBlock>{jolly}</DescriptionBlock>}
            </Description>
          )}

          {(variants.length > 1 || showSingleVariant) && (
            <ApparelVariants
              onClick={(e) => {
                e.preventDefault()
              }}
            >
              <ColorSelector
                center
                variants={variants}
                currentVariant={selectedVariant}
                onClick={setSelectedVariant}
              />
            </ApparelVariants>
          )}
          <Separator />
          <PriceRow>
            <Price
              data-testid={dataTestIds.PRODUCT_PRICE_IN_CARD}
              dangerouslySetInnerHTML={{
                __html: price,
              }}
            />
            {isPriceDiscounted && (
              <Price
                isDiscounted
                dangerouslySetInnerHTML={{
                  __html: compareAtPrice,
                }}
              />
            )}
          </PriceRow>
        </LinkFlex>
      </ApparelCard>
      {isQuickAdd && (
        <QuickAdd
          shouldOpenCartDrawerOnAddToCart
          compareAtPrice={compareAtPrice}
          isPriceDiscounted={isPriceDiscounted}
          staffPickIcon={staffPickIcon}
          title={title || ''}
          price={price}
          productType={productType}
          description={description}
          isGiftCard={isGiftCard}
          sizes={sizes}
          handle={selectedVariant.handle}
          isOpen={isQuickAddPopupOpen}
          image={
            selectedVariant?.img?.front
              ? {
                  src: selectedVariant?.img?.front,
                  alt: `${selectedVariant.name}`,
                }
              : null
          }
          onAddToCart={onAddToCart}
          onClose={() => {
            setIsQuickAddPopupOpen(false)
          }}
        />
      )}
    </ThemeProvider>
  )
}

interface QuickAddItemDesktopWrapperProps {
  title: string
  sizeGuideUrl: string | null
  sizeGuideText: string
  isOpen?: boolean
  sizes?: ProductSize[]
  isGiftCard?: boolean
  handle: Product['handle']
  onAddToCart?: (handle: Product['handle']) => void
  shouldOpenCartDrawerOnAddToCart: boolean
}

const QuickAddItemDesktopWrapper = ({
  title,
  sizeGuideUrl,
  sizeGuideText,
  isOpen = false,
  sizes = [],
  isGiftCard = false,
  onAddToCart,
  handle,
  shouldOpenCartDrawerOnAddToCart,
}: QuickAddItemDesktopWrapperProps) => {
  const [dynamicSizes, setDynamicSizes] = useState<ProductSize[]>(sizes)
  const [status, setStatus] = useState<Status>('idle')
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  const { addToCart, setIsDrawerOpen } = useCart()

  const handleSizeSelect = (id: string) => {
    setStatus('loading')
    setSelectedSize(id)

    addToCart(id, 1)
      .then(() => {
        setDynamicSizes((prevSizes) => {
          const updatedSizes = prevSizes.map((size) =>
            size.id === id
              ? {
                  ...size,
                  quantityAvailable: size.quantityAvailable - 1,
                }
              : size,
          )

          return updatedSizes
        })
        setStatus('success')
        setTimeout(() => {
          if (shouldOpenCartDrawerOnAddToCart) {
            window.location.pathname !== '/cart' && setIsDrawerOpen(true)
          }
        }, 1000)
      })
      .catch((error) => {
        handleError(error, { message: "Couldn't add product to cart." })
        setStatus('error')
      })
      .finally(() => {
        onAddToCart && onAddToCart(handle)
        setTimeout(() => {
          setSelectedSize(null)
          setStatus('idle')
        }, 1000) // same duration as svg animation
      })
  }

  return (
    <SlideUp>
      <QuickAddToCartDesktop
        title={title}
        sizeGuideUrl={sizes.length > 1 ? sizeGuideUrl : null}
        sizeGuideText={sizeGuideText}
        sizes={dynamicSizes}
        status={status}
        selectedSize={selectedSize}
        isOpen={isOpen}
        isGiftCard={isGiftCard}
        onSizeSelect={handleSizeSelect}
      />
    </SlideUp>
  )
}
